/**
 * Scheduled-arrival window refusals (CD32 — owner screenshot, 2026-09-01 11:34).
 *
 * <p><strong>The defect.</strong> A customer whose session was SCHEDULED for
 * 12:30 opened their link at 11:34 and walked straight into the live waiting-room
 * queue — "#2 in line" — because nothing enforced the join window. The be lane
 * restores the SPEC-07 §3 contract: with a confirmed booking, queue entry answers
 * a TYPED problem instead of admitting the customer.
 *
 *   {@code {errorCode:"ARRIVAL_BEFORE_WINDOW", windowOpensAt, slotStart, slotEnd}}
 *   {@code {errorCode:"ARRIVAL_WINDOW_CLOSED",                slotStart, slotEnd}}
 *
 * <p>Only QUEUE ENTRY refuses. The pre-steps (consent, capture, checks) stay open
 * before the window by design, and a walk-in — no booking — is never refused, so
 * this module never sees a problem body at all on that path.
 *
 * <p><strong>Tolerance is the point of the parser.</strong> It branches on the
 * PRESENCE of a known `errorCode` and nothing else: an older be, which answers
 * these situations with untyped prose or does not refuse at all, yields null here
 * and the waiting room behaves exactly as it does today. Every field is validated
 * before it is trusted — this is untrusted wire input rendered to a member of the
 * public — and a refusal we cannot render honestly degrades to null rather than
 * putting a broken screen in front of someone who is trying to get verified.
 */

import { ApiError } from '@/api/client';

export const ARRIVAL_BEFORE_WINDOW = 'ARRIVAL_BEFORE_WINDOW';
export const ARRIVAL_WINDOW_CLOSED = 'ARRIVAL_WINDOW_CLOSED';

/** The customer arrived too early: the window has a known opening instant to count down to. */
export interface ArrivalBeforeWindow {
  kind: 'before';
  /** When queue entry starts being accepted — the countdown target. Always valid ISO. */
  windowOpensAt: string;
  slotStart: string | null;
  slotEnd: string | null;
}

/** The customer arrived after the window closed: a new time is the only way forward. */
export interface ArrivalWindowClosed {
  kind: 'closed';
  slotStart: string | null;
  slotEnd: string | null;
}

export type ArrivalRefusal = ArrivalBeforeWindow | ArrivalWindowClosed;

/**
 * Read a typed arrival refusal out of a failed queue-entry call, or null when
 * this is not one (older backend, a different refusal, a network fault).
 */
export function parseArrivalRefusal(err: unknown): ArrivalRefusal | null {
  const problem = problemOf(err);
  if (problem === null) return null;

  const errorCode = stringField(problem, 'errorCode');
  const slotStart = isoField(problem, 'slotStart');
  const slotEnd = isoField(problem, 'slotEnd');

  if (errorCode === ARRIVAL_BEFORE_WINDOW) {
    // The countdown needs a target. `windowOpensAt` is the contract; slotStart is
    // the only honest stand-in (a window that opens at its slot start), and with
    // neither there is nothing to count down to — degrade to today's behaviour.
    const windowOpensAt = isoField(problem, 'windowOpensAt') ?? slotStart;
    if (windowOpensAt === null) return null;
    return { kind: 'before', windowOpensAt, slotStart, slotEnd };
  }
  if (errorCode === ARRIVAL_WINDOW_CLOSED) {
    // No time is REQUIRED here: "your window has passed" plus a reschedule is a
    // complete, honest screen even if the slot times were unreadable.
    return { kind: 'closed', slotStart, slotEnd };
  }
  return null;
}

/** Milliseconds until `iso`, clamped at zero. A past (or unreadable) instant is "now". */
export function msUntil(iso: string, nowMs: number): number {
  const target = Date.parse(iso);
  if (!Number.isFinite(target)) return 0;
  return Math.max(0, target - nowMs);
}

/**
 * A countdown, mono-friendly: `MM:SS` under an hour, `H:MM:SS` above it.
 *
 * Seconds round UP so a fresh 90-second countdown opens at 01:30 and not 01:29,
 * and it reaches 00:00 only when the window is genuinely open.
 */
export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`;
}

// ── untrusted-body plumbing ─────────────────────────────────────────────────

/**
 * The problem body as a flat bag of fields, or null when there is none.
 *
 * Spring writes ProblemDetail extras at the TOP level of the JSON; some
 * serialisers nest them under `properties`. Both are accepted, top level wins,
 * because guessing wrong would mean rendering the waiting-room queue at a
 * customer the be just refused.
 */
function problemOf(err: unknown): Record<string, unknown> | null {
  if (!(err instanceof ApiError)) return null;
  const body = err.body;
  if (body === null || typeof body !== 'object' || Array.isArray(body)) return null;
  const top = body as Record<string, unknown>;
  const nested = top['properties'];
  if (nested !== null && typeof nested === 'object' && !Array.isArray(nested)) {
    return { ...(nested as Record<string, unknown>), ...top };
  }
  return top;
}

function stringField(problem: Record<string, unknown>, name: string): string | null {
  const value = problem[name];
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/** A field that must be a parseable instant to be worth rendering. */
function isoField(problem: Record<string, unknown>, name: string): string | null {
  const value = stringField(problem, name);
  if (value === null) return null;
  return Number.isFinite(Date.parse(value)) ? value : null;
}
